import * as React from "react";
import {PluginTemplates} from "../utils/PluginTemplates";
import * as moment from "moment";
import {lazyInject} from "@athena-portal/core";
import {ICustomerPortalService, PaymentsResponse, Types, Subscription, Policy, productHelper, PaymentDetails, INTERACTION_PROCESS_CHANGE_ADDRESS, INTERACTION_PROCESS_MTA_ADD_CAR} from "@falcon/services";
import {Overlay} from "@athena-portal/components";
import {getAmountToDisplay} from "../CommonFunctions";

interface IProps {
    quoteInteraction?: any
}

interface IState {
    payments?: PaymentsResponse,
    subscription?: Subscription,
    policies?: Policy[]
}

export class PricingExplainedInfo extends React.Component<IProps, IState> {

    private includesMotorProduct: boolean;
    private includesRentersProduct: boolean;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);
        this.state = {}
    }

    public componentDidMount(): void {
        this.customerPortalService.getPaymentHistory().subscribe(
            (payments: PaymentsResponse) => {
                this.setState({payments})
            }
        );

        this.customerPortalService.getSubscription().subscribe(
            (subscription: Subscription) => {
                this.setState({subscription});
            }
        );

        this.customerPortalService.getPolicies().subscribe(
            (policies: Policy[]) => {
                this.setState({policies});
                this.includesMotorProduct = productHelper.includesMotorProduct(policies);
                this.includesRentersProduct = productHelper.includesRentersProduct(policies);
            }
        );
    }

    public render(): React.ReactNode {
        if (this.state.payments && this.state.subscription && this.state.policies) {
            return (
                <>
                    <p className="font-weight-extra-light mb-3" role="alert">
                        Your price could go up or down after first month but we guarantee you won't pay more than <span className="font-weight-semi-bold">
                        {getAmountToDisplay(this.getNeverHigherAmount())} per month until {PluginTemplates.convertDate(this.state.subscription.subscriptionPeriodEndDate, "DD MMMM YYYY")}
                        </span> - see below subscription plan for 3 months
                    </p>
                    <div className="mx-auto mb-4 premium-calendar-section">
                        <div className="row no-gutters">
                            {this.renderPremiumCalendar()}
                        </div>
                    </div>
                    <p className="font-weight-extra-light mb-4">All figures include Insurance Premium Tax</p>

                    <h4 className="font-weight-semi-bold fs-18 text-blue-2">Why can the price change?</h4>
                    <p className="font-weight-extra-light mb-4">
                        Because Flow works on a month-by-month basis, risk factors are reviewed more often than an
                        annual insurance policy. This means your price can change.
                    </p>
                    <p className="font-weight-extra-light mb-4">
                        The good news is we’ll give you a guaranteed maximum price every three months, and you’ll get our
                        best price whether you’re a new or existing Flow customer.
                    </p>
                    <p className="font-weight-extra-light mb-4">
                        Plus, you won’t be paying for any admin or cancellation fees – just great quality cover.
                    </p>
                    <p className="font-weight-extra-light mb-4">
                        Your guaranteed maximum price could change during this time if you make any changes to your
                        subscription e.g. move address or add extra cover.
                    </p>

                    <h4 className="font-weight-semi-bold fs-18 text-blue-2">What things affect my price?</h4>
                    <ul className="font-weight-extra-light">
                        <li>Your age</li>
                        <li>Any claims you’ve made</li>
                        {this.includesMotorProduct && <>
                            <li>Your car getting older</li>
                            <li>The cost of repairing your vehicle including the parts and labour needed</li>
                            <li>A change in your car’s grouping driven by its overall claims performance</li>
                        </>}
                        {this.includesRentersProduct && <>
                            <li>Moving house</li>
                            <li>Adding extra cover</li>
                        </>}
                    </ul>

                    <h4 className="font-weight-semi-bold fs-18 text-blue-2">What happens after the first three months?</h4>
                    <p className="font-weight-extra-light mb-4">
                        We’ll email you seven days before your first three months are up and give you a new guaranteed
                        price. You’ll be able to find it by logging into your account, too.
                    </p>
                    <p className="font-weight-extra-light mb-4">
                        Don't worry – this price shouldn't change much unless you've made a claim or there's been a major
                        change in your circumstances.
                    </p>

                    <h4 className="font-weight-semi-bold fs-18 text-blue-2">What happens if I'm not happy with my new price?</h4>
                    <p className="font-weight-extra-light mb-4">
                        We hate goodbyes. But if you need to cancel your cover you can do so without paying a cancellation
                        fee. Just log into your account and follow the simple steps to ‘Cancel my subscription’.
                    </p>
                </>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private renderPremiumCalendar = (): JSX.Element => {
        const {subscription} = this.state;
        const neverHigherAmount: number = this.getNeverHigherAmount();
        const month1: moment.Moment = moment(subscription!.subscriptionPeriodEndDate).subtract(3, 'M');
        const month2: moment.Moment = moment(subscription!.subscriptionPeriodEndDate).subtract(2, 'M');
        const month3: moment.Moment = moment(subscription!.subscriptionPeriodEndDate).subtract(1, 'M');


        if (moment().isBefore(month2)) {
            return (
                <>
                    {this.renderCalendarMonth("pr-3", month1.format('MMMM'), "You already paid", this.getPaidAmount(month1))}
                    {this.renderCalendarMonth("px-2", month2.format('MMMM'), "You won't pay more than", neverHigherAmount)}
                    {this.renderCalendarMonth("pl-3", month3.format('MMMM'), "You won't pay more than", neverHigherAmount)}
                </>
            );
        } else if (moment().isBefore(month3)) {
            return (
                <>
                    {this.renderCalendarMonth("pr-3", month1.format('MMMM'), "You already paid", this.getPaidAmount(month1))}
                    {this.renderCalendarMonth("px-2", month2.format('MMMM'), "You already paid", this.getPaidAmount(month2))}
                    {this.renderCalendarMonth("pl-3", month3.format('MMMM'), "You won't pay more than", neverHigherAmount)}
                </>
            );
        } else {
            return (
                <>
                    {this.renderCalendarMonth("pr-3", month1.format('MMMM'), "You already paid", this.getPaidAmount(month1))}
                    {this.renderCalendarMonth("px-2", month2.format('MMMM'), "You already paid", this.getPaidAmount(month2))}
                    {this.renderCalendarMonth("pl-3", month3.format('MMMM'), "You already paid", this.getPaidAmount(month3))}
                </>
            );
        }
    };

    private renderCalendarMonth = (className: string, month: string, text: string, premium: number): JSX.Element => (
        <div className={`col-4 ${className}`}>
            <div className="row no-gutters premium-calendar-holders">
                <div className="col-4 pr-1">
                    <div className="col-4"/>
                </div>
                <div className="col-4"/>
                <div className="col-4 pl-1">
                    <div className="col-4"/>
                </div>
            </div>
            <div className="bg-white-blue overflow-auto premium-calendar">
                <div className="font-weight-normal text-center fs-14 mt-2 mb-1">
                    {month}
                </div>
                <div className="bg-white py-1 calendar-body p-1">
                    <div className="text-center fs-14">
                        {text}
                    </div>
                    <div className="text-center text-blue-2 font-weight-light">
                        <span className="fs-24">{getAmountToDisplay(premium)}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    private getPaidAmount = (month: moment.Moment): number => {
        const renewalPayments: any[] = this.state.payments!.paymentDetailsList
            .filter((payment: PaymentDetails) => payment.policyVersion[0].summary === "RENEWAL")
            .filter((payment: PaymentDetails) => moment(month).isSame(payment.date, 'M'));

        if (renewalPayments.length > 0) {
            return renewalPayments[0].amount;
        }

        return this.state.subscription!.lastPaymentAmount;
    };

    private getNeverHigherAmount = (): number => {
        // if Quote info
        if (this.props.quoteInteraction) {

            // if new policy Quote
            if (this.props.quoteInteraction.process === INTERACTION_PROCESS_MTA_ADD_CAR) {
                return this.state.subscription!.lastNeverHigherAmount + this.props.quoteInteraction.factors.neverHigher;
            }

            // if mtaChangeAddress Quote
            if (this.props.quoteInteraction.process === INTERACTION_PROCESS_CHANGE_ADDRESS) {
                return this.props.quoteInteraction.factors.neverHigher;
            }

            // if Quote changes for existing policy (e.g. mta, mtaReplaceCar)
            if (this.props.quoteInteraction.agreementNumber) {
                return this.state.policies!
                    .filter(policy => policy.policyNumber !== this.props.quoteInteraction.agreementNumber)
                    .map(policy => policy.interaction.factors.neverHigher)
                    .reduce((total, next) => total + next, this.props.quoteInteraction.factors.neverHigher);
            }

            // if Quote changes for all existing policies
            return this.props.quoteInteraction.factors.neverHigher;
        }

        // if Subscription info
        return this.state.subscription!.lastNeverHigherAmount;
    };
}




// WEBPACK FOOTER //
// ./src/components/PricingExplainedInfo.tsx